// src/pages/admin/SeedDesks.jsx — FINAL (seed all desks from floor maps)
import React, { useEffect, useMemo, useState } from 'react';
import { motion } from 'framer-motion';
import Card from '../../components/Card.jsx';
import Button from '../../components/Button.jsx';
import LoadingSpinner from '../../components/LoadingSpinner.jsx';
import toast from 'react-hot-toast';
import { api } from '../../lib/api.js';
import { DESK_MAPS, DESK_FLOOR_ORDER } from '../../floormaps/desks.js';
import { Monitor, Check, UploadCloud } from 'lucide-react';

const pad2 = (n) => String(n).padStart(2, '0');

export default function SeedDesks() {
  const [loading, setLoading] = useState(true);
  const [floors, setFloors] = useState([]);
  const [existing, setExisting] = useState([]);
  const [busy, setBusy] = useState(false);
  const [created, setCreated] = useState(0);

  // ambil lantai & resource desk yg sudah ada
  const refresh = async () => {
    setLoading(true);
    try {
      const [floRes, resRes] = await Promise.all([
        api.get('/floors'),
        api.get('/admin/resources', { params: { type: 'Desk' } }),
      ]);
      setFloors(floRes.data || []);
      setExisting(resRes.data || []);
    } catch (e) {
      console.error(e);
      toast.error('Failed to load floors/resources.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { refresh(); }, []);

  const floorIdByName = useMemo(() => {
    const m = new Map();
    (floors || []).forEach(f => m.set(f.name, f.id));
    return m;
  }, [floors]);

  const existingKey = useMemo(() => {
    // kunci unik: floor_id + name (supaya "01" di Floor 1 & "01" di Floor 2 dianggap beda)
    const s = new Set();
    (existing || []).forEach(r => {
      if (r.type_name === 'Desk') s.add(`${r.floor_id || 'NULL'}::${r.name}`);
    });
    return s;
  }, [existing]);

  // hitung item yg akan dibuat
  const plan = useMemo(() => {
    const tasks = [];
    for (const floorName of DESK_FLOOR_ORDER) {
      const map = DESK_MAPS[floorName];
      if (!map) continue;

      const floor_id = floorIdByName.get(floorName) || null;
      const seats = Array.isArray(map?.seats) ? map.seats : (map?.seats || []);
      // urutkan dulu (biar rapih)
      const ordered = [...seats].sort((a, b) =>
        a.y === b.y ? a.x - b.x : a.y - b.y
      );

      ordered.forEach((s) => {
        const name = String(s.id).trim();
        const key = `${floor_id || 'NULL'}::${name}`;
        if (!existingKey.has(key)) {
          tasks.push({
            name,
            type_name: 'Desk',
            floor_id,
            position_x: Math.round(+s.x || 0),
            position_y: Math.round(+s.y || 0),
            is_active: true,
          });
        }
      });
    }
    return tasks;
  }, [floorIdByName, existingKey]);

  const seedAll = async () => {
    if (!plan.length) {
      toast('Nothing to create — all desks are already present.');
      return;
    }
    setBusy(true);
    setCreated(0);
    try {
      let ok = 0, fail = 0;
      // Jalankan bertahap (tidak diborong) supaya aman ke server
      for (const t of plan) {
        try {
          await api.post('/admin/resources', t);
          ok++;
          setCreated(ok);
        } catch (err) {
          console.error('Create failed for', t.name, err);
          fail++;
        }
      }
      if (ok && !fail) toast.success(`Created ${ok} desk resources.`);
      else if (ok && fail) toast(`Partial: ${ok} created, ${fail} failed.`, { icon: '⚠️' });
      else toast.error('Failed to create desks.');
      await refresh();
    } finally {
      setBusy(false);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64"><LoadingSpinner size="xl" /></div>;
  }

  return (
    <div className="space-y-6">
      <motion.div initial={{opacity:0,y:-20}} animate={{opacity:1,y:0}} className="flex items-center justify-between">
        <div className="flex items-center">
          <Monitor className="h-8 w-8 text-emerald-400 mr-3" />
          <div>
            <h1 className="text-2xl font-bold text-white">Seed Desks from Floor Maps</h1>
            <p className="text-gray-400">Generate missing “Desk” resources from the JSON floor maps so they’re bookable.</p>
          </div>
        </div>
        <div className="text-right">
          <div className="text-sm text-gray-400">Existing desks: <span className="text-white font-semibold">{existing.length}</span></div>
        </div>
      </motion.div>

      <Card className="bg-gradient-to-br from-gray-800 to-gray-900 border-gray-600">
        <div className="mb-4">
          <h2 className="text-lg font-semibold text-white mb-1">Plan</h2>
          <p className="text-gray-400 text-sm">
            Floors detected: <span className="text-white font-medium">{floors.length}</span> •
            Desks to create: <span className="text-white font-medium">{plan.length}</span>
          </p>
        </div>

        <div className="flex flex-wrap gap-2 mb-4">
          {DESK_FLOOR_ORDER.map((f) => (
            <span key={f} className="px-2 py-1 bg-gray-700/50 rounded text-xs text-gray-300">
              {f} {floorIdByName.get(f) ? '' : '(no floor id)'}
            </span>
          ))}
        </div>

        <div className="flex items-center gap-3">
          <Button onClick={seedAll} loading={busy} className="flex items-center">
            <UploadCloud className="h-4 w-4 mr-2" />
            Create Missing Desks
          </Button>
          <Button variant="secondary" onClick={refresh} disabled={busy} className="flex items-center">
            <Check className="h-4 w-4 mr-2" />
            Re-scan
          </Button>
          {busy ? <span className="text-sm text-gray-400">Created: {created}/{plan.length}</span> : null}
        </div>

        <div className="mt-6 text-xs text-gray-400">
          <p>Notes:</p>
          <ul className="list-disc list-inside">
            <li>Desk <code>name</code> diambil dari <code>id</code> pada seats JSON (harus sama dengan yang dipakai di peta).</li>
            <li>Floor dicocokkan berdasarkan <strong>nama lantai</strong> di <code>DESK_FLOOR_ORDER</code> & <code>DESK_MAPS</code>. Pastikan sudah ada lantainya di DB.</li>
            <li><code>position_x/y</code> disimpan dari koordinat kursi (opsional, hanya metadata).</li>
          </ul>
        </div>
      </Card>
    </div>
  );
}
